import React, { useEffect } from 'react';
import { FormattedMessage, FormattedDate, IntlShape } from 'react-intl';
import styled from 'styled-components';
import {
  Box,
  Text,
  H2,
  Image,
  Anchor,
  Button,
  I18NLogOut,
  Grid,
  GridCell,
  Alert,
  AnchorButton
} from '../../../components';
import { pushRoute } from '../../../components/Link';
import { ProductPrice } from '../../../components/ProductPlanCard';
import { VerifyPhoneNumber } from '../../VerifyPhoneNumber';
import { useAuth } from '../../../lib/with-auth';
import messages from '../../VerifyPhoneNumber/messages';
import {
  CurrentUser,
  productBySlugQuery_system,
  productBySlugQuery_productBySlug,
  VerificationMode
} from '../../../core-types';
import router from 'next/router';
import { hideproductSlugFromRequest } from '../../../lib/utils';
import ReactMarkdown from 'react-markdown';

type Props = {
  product: productBySlugQuery_productBySlug;
  productSlug: string;
  privacyPageSlug: string | null;
  intl: IntlShape;
  step?: string;
  currentUser: CurrentUser | null;
  system: productBySlugQuery_system;
};

const OverflowImage = styled.div`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    overflow: hidden;
    height: 200px;
    border-radius: 10px;
    img {
      margin-top: -70px;
    }
  }
`;

const PhoneStep = ({
  product,
  productSlug,
  privacyPageSlug,
  intl,
  step,
  currentUser,
  system
}: Props) => {
  const { logOut } = useAuth();

  //This is a hack for ATB/OBOS campaigns. Remove when campaign is over.
  useEffect(() => {
    const productSlug = router?.query.productSlug;
    hideproductSlugFromRequest(productSlug);
  }, []);

  let VerificationComponent = (
    <VerifyPhoneNumber
      mode={VerificationMode.signup}
      center={false}
      onLogIn={() => {
        pushRoute({
          intl,
          route: 'buy',
          params: {
            productSlug,
            step: 0
          }
        });
      }}
      label={
        <FormattedMessage
          {...messages.phoneNumberInfo}
          values={{
            privacyInformation: privacyPageSlug && (
              <Anchor href={`/${intl.locale}/${privacyPageSlug}`}>
                <FormattedMessage {...messages.personalInformation} />
              </Anchor>
            )
          }}
        />
      }
    />
  );

  if (currentUser) {
    VerificationComponent = (
      <>
        <Text as="p" data-testid="Checkout__CurrentUser__Alert">
          <FormattedMessage
            {...messages.alreadyLoggedIn}
            values={{
              phoneNumber: currentUser && currentUser.parsedPhoneNumber && (
                <Text as="span" bold>
                  {currentUser.parsedPhoneNumber.formattedNumber}
                </Text>
              )
            }}
          />
        </Text>
        <Box mb="sm">
          <Button
            data-testid="continueWithThisMember"
            variant="primary"
            shape="cool"
            onClick={() =>
              pushRoute({
                intl,
                route: 'buy',
                params: {
                  productSlug,
                  step: 0
                }
              })
            }
          >
            <FormattedMessage {...messages.continueWithThisMember} />
          </Button>
        </Box>
        <Text as="p">
          <FormattedMessage
            {...messages.useOtherPhoneNumber}
            values={{
              logOutButton: (
                <AnchorButton
                  display="inline"
                  onClick={() => logOut()}
                  color="link"
                >
                  <I18NLogOut />
                </AnchorButton>
              )
            }}
          />
        </Text>
      </>
    );
  }

  if (step === 'verification') {
    return (
      <>
        <H2 mb="sm" mt="lg" typoStyle="md" textAlign="center">
          <FormattedMessage {...messages.oneTime} />
        </H2>
        {VerificationComponent}
      </>
    );
  }

  return (
    <Grid justifyContent="center">
      {product.image && product.image.url && (
        <GridCell width={[1, 1, 1, 2 / 5]}>
          <OverflowImage>
            <Image
              boxShadow="heavy"
              borderRadius="lg"
              src={product.image.url}
            />
          </OverflowImage>
        </GridCell>
      )}
      <GridCell
        width={[1, 1, 1, 3 / 5]}
        px={[3, 3, 3, 6]}
        flexDirection="column"
        height="100%"
      >
        <Box flex={1} mb="md">
          <Box mb="sm">
            <H2
              mb={0}
              typoStyle="md"
              mt={[4, 4, 4, 0]}
              data-testid="Checkout__Heading"
            >
              {product.name}
            </H2>
            <ProductPrice {...product} />
            {product.descriptionText && (
              <Text as="p" mb="sm" whiteSpace="pre-line">
                <ReactMarkdown>{product.descriptionText}</ReactMarkdown>
              </Text>
            )}
            {!system.inSeason &&
              system.nextSeason &&
              system.nextSeason.startDate && (
                <Alert variant="info" mb="xs" mt="xs">
                  <FormattedMessage
                    id="system_opens_at_date"
                    defaultMessage="The system opens at {date}"
                    values={{
                      date: (
                        <FormattedDate
                          year="numeric"
                          month="long"
                          day="numeric"
                          value={system.nextSeason.startDate}
                        />
                      )
                    }}
                  />
                </Alert>
              )}
          </Box>
          {VerificationComponent}
        </Box>
      </GridCell>
    </Grid>
  );
};

export default PhoneStep;
